import AppConstants from "@/constants/AppConstants";
import {
  RECTANGLE_BACKGROUND_COLOR,
  RECTANGLE_GRADIENT_COLOR_1,
  RECTANGLE_GRADIENT_COLOR_2,
  BACKDROP_FILTER,
  RECTANGLE_FILL_TYPE,
} from "@/constants/MergeConstants";
import { FILL_TYPE_COLOR, FILL_TYPE_GRADIENT } from "@/constants/SyncConstants";
import { MIXED } from "@/constants/DesignConstants.mjs";
import { getColorPartsFromString } from "@/utils/ColorUtils";
import { ADVANCED_FILL_DEFAULT_BACKDROP_FILTER } from "@/constants/AdvancedFillModePresetConstants";
import { hasBackdropFilter } from "./FilterUtils";

const { TRANSPARENT, UPDATE_ACTION_DELETE, UPDATE_ACTION_SET } = AppConstants;
const DEFAULT_PALETTE_COLOR = "colors-1";

/**
 * Extracts a single base color from a color string (e.g. from a gradient).
 * Returns fallback when there is no color or value is MIXED; optionally returns TRANSPARENT when empty.
 *
 * @param {string} value - Color string (e.g. gradient or solid) to parse.
 * @param {object} [options] - Options.
 * @param {string} [options.fallback='colors-1'] - Color to return when missing or MIXED.
 * @param {boolean} [options.allowTransparent=false] - If true, return TRANSPARENT when no color; otherwise use fallback.
 * @returns {string} Resolved base color, fallback, or TRANSPARENT.
 */
function coerceBaseColor(
  value,
  { fallback = DEFAULT_PALETTE_COLOR, allowTransparent = false } = {},
) {
  const baseColor = getColorPartsFromString(value).color;

  if (!baseColor) return allowTransparent ? TRANSPARENT : fallback;
  if (baseColor === MIXED) return fallback;

  return baseColor;
}

/**
 * Computes the dependent property updates needed when a rectangle element's fill type changes.
 *
 * This is used to:
 * - initialize gradient colors when switching to gradient fill
 * - migrate gradient → solid color when switching to color fill
 * - clear gradient-only props when switching away from gradient fill
 *
 * @param {Object} params
 * @param {string} params.newFillType - Target fill type (e.g. "color" | "gradient")
 * @param {(propName: string) => any} params.getValue - Getter for the current value of a prop.
 * @returns {Array<[string, any] | [string, any, string]>} List of updates as `[propName, value]` or `[propName, value, action]` tuples.
 */
export function getFillTypeTransitionUpdates({ newFillType, getValue }) {
  const isAdvancedFill = hasBackdropFilter(getValue(BACKDROP_FILTER));
  const DELETE_BACKDROP_FILTER = /** @type {[string, any, string]} */ ([
    BACKDROP_FILTER,
    null,
    UPDATE_ACTION_DELETE,
  ]);

  if (newFillType === FILL_TYPE_GRADIENT) {
    const rectColor = coerceBaseColor(getValue(RECTANGLE_BACKGROUND_COLOR), {
      fallback: DEFAULT_PALETTE_COLOR,
      allowTransparent: true,
    });

    /** @type {[string, any][]} */
    const updates = [
      [RECTANGLE_GRADIENT_COLOR_1, rectColor],
      [RECTANGLE_GRADIENT_COLOR_2, `${rectColor}:50`],
    ];
    return isAdvancedFill ? [DELETE_BACKDROP_FILTER, ...updates] : updates;
  }

  if (newFillType === FILL_TYPE_COLOR) {
    const solid = coerceBaseColor(
      getValue(
        hasBackdropFilter(getValue(BACKDROP_FILTER))
          ? RECTANGLE_BACKGROUND_COLOR
          : RECTANGLE_GRADIENT_COLOR_1,
      ),
      {
        fallback: DEFAULT_PALETTE_COLOR,
        allowTransparent: false,
      },
    );

    /** @type {[string, any][]} */
    const updates = [
      [RECTANGLE_BACKGROUND_COLOR, solid],
      [RECTANGLE_GRADIENT_COLOR_1, null],
      [RECTANGLE_GRADIENT_COLOR_2, null],
    ];
    return isAdvancedFill ? [DELETE_BACKDROP_FILTER, ...updates] : updates;
  }

  return [];
}

/**
 * Returns prop updates for switching to or from advanced fill: clears/sets backdrop filter and clears fill type as needed.
 *
 * @param {Object} params
 * @param {(propName: string) => any} params.getValue - Getter function to get the current value of a prop
 * @param {(propName: string) => boolean} params.hasProp - Function to check if a prop exists in the state
 * @typedef {[string, any, string?]} AdvancedFillUpdate - [propName, value, action?]; action is UPDATE_ACTION_DELETE or UPDATE_ACTION_SET
 * @returns {AdvancedFillUpdate[]}
 */
export function getAdvancedFillTransitionUpdates({ getValue, hasProp }) {
  const backdropFilter = getValue(BACKDROP_FILTER);
  const hasBackdropInState = hasBackdropFilter(backdropFilter);

  /** @type {AdvancedFillUpdate[]} */
  const updates = hasProp(RECTANGLE_FILL_TYPE)
    ? [[RECTANGLE_FILL_TYPE, null, UPDATE_ACTION_DELETE]]
    : [];
  if (hasProp(RECTANGLE_GRADIENT_COLOR_1)) {
    updates.push([
      RECTANGLE_BACKGROUND_COLOR,
      getValue(RECTANGLE_GRADIENT_COLOR_1),
      UPDATE_ACTION_SET,
    ]);
  }

  const defaultBackdrop = ADVANCED_FILL_DEFAULT_BACKDROP_FILTER;
  if (!hasBackdropInState) {
    updates.push([BACKDROP_FILTER, defaultBackdrop, UPDATE_ACTION_SET]);
  }

  return updates;
}
